import { Avatar, Badge, Button, Group, Menu, Stack, Text, Title } from '@mantine/core';
import { IconAlertTriangle, IconChevronDown, IconMail, IconPencil, IconPhone, IconPlus, IconTransfer } from '@tabler/icons-react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { HumanNameDisplay, ContactPointDisplay } from '@medplum/react';
import type { HumanName, Patient } from '@medplum/fhirtypes';
import type { Client } from '../../caseload/data';
import {
  clientToPatient,
  getClientPhone,
  getClientEmail,
  getClientOfficialName,
  getClientPreferredName,
} from '../../../lib/clientToPatient';
interface ClientHeaderProps {
  client: Client;
  /** Optional FHIR Patient resource - if provided, uses FHIR data directly */
  patient?: Patient;
  /** Called when user toggles high-risk flag; if not provided, option is hidden */
  onToggleHighRisk?: () => void;
  /** Whether the client is currently flagged as high risk */
  isHighRisk?: boolean;
  /** Called when user selects "Add EoC" from Actions dropdown */
  onAddEpisode?: () => void;
  /** Called when user selects "Transfer" from Actions dropdown */
  onTransfer?: () => void;
}

/**
 * Client header component displaying client info and quick actions.
 * Uses Mantine styling with Medplum display components for FHIR compatibility.
 * 
 * This component uses our custom layout but leverages Medplum's display components
 * (HumanNameDisplay, ContactPointDisplay) so that when connected to a real FHIR
 * backend, the data will render correctly.
 */
export function ClientHeader({
  client,
  patient: externalPatient,
  onToggleHighRisk,
  isHighRisk,
  onAddEpisode,
  onTransfer,
}: ClientHeaderProps): JSX.Element {
  // Convert client to FHIR Patient if not provided externally
  const patient = useMemo(
    () => externalPatient || clientToPatient(client),
    [client, externalPatient]
  );

  // Get FHIR-compatible name and contact structures
  const officialName = useMemo(() => getClientOfficialName(client), [client]);
  const preferredName = useMemo(() => getClientPreferredName(client), [client]);
  const phoneContact = useMemo(() => getClientPhone(client), [client]);
  const emailContact = useMemo(() => getClientEmail(client), [client]);
  const officialNameForDisplay = (officialName ?? ({ text: client.name } as HumanName));

  // Generate initials from preferred name or fall back to official name
  const initials = useMemo(() => {
    if (preferredName) {
      const given = preferredName.given?.[0]?.charAt(0) || '';
      const family = preferredName.family?.charAt(0) || '';
      return `${given}${family}`.toUpperCase();
    }
    const given = officialName?.given?.[0]?.charAt(0) || '';
    const family = officialName?.family?.charAt(0) || '';
    return `${given}${family}`.toUpperCase() || client.name.charAt(0).toUpperCase();
  }, [preferredName, officialName, client.name]);

  return (
    <Group justify="space-between" align="flex-start">
      <Group>
        <Avatar size="lg" radius="md" color="gold">
          {initials}
        </Avatar>
        <Stack gap={4}>
          <Group gap="md" align="flex-end" wrap="wrap">
            <Stack gap={2}>
              <Text size="xs" c="gray.6" tt="uppercase" fw={600}>
                Preferred name
              </Text>
              <Title order={3} style={{ lineHeight: 1.2 }}>
                {preferredName ? (
                  <HumanNameDisplay value={preferredName} />
                ) : (
                  <HumanNameDisplay value={officialNameForDisplay} />
                )}
              </Title>
            </Stack>
            {preferredName && (
              <Stack gap={2} style={{ paddingBottom: 6 }}>
                <Text size="xs" c="gray.6" tt="uppercase" fw={600}>
                  Legal name
                </Text>
                <Text size="md" c="gray.6" style={{ lineHeight: 1.2 }}>
                  <HumanNameDisplay value={officialNameForDisplay} />
                </Text>
              </Stack>
            )}
            <Group gap="xs" align="center" style={{ paddingBottom: 6 }}>
              <Text size="md" c={client.pronouns ? 'gray.6' : 'gray.5'}>
                ({client.pronouns || 'Pronouns'})
              </Text>
              <Badge
                color={patient.active !== false ? 'green' : 'gray'}
                variant="light"
                size="sm"
              >
                {patient.active !== false ? 'Active' : 'Inactive'}
              </Badge>
              {((client.highRiskFlags && client.highRiskFlags.length > 0) ||
                (client.riskTags && client.riskTags.length > 0)) && (
                  <Badge color="red" variant="light" size="sm">
                    HIGH RISK
                  </Badge>
                )}
              {client.insuranceActive === false && (
                <Badge color="orange" variant="light" size="sm">
                  Inactive Insurance
                </Badge>
              )}
            </Group>
          </Group>
          <Group gap="md">
            <Group gap={6} miw={140}>
              <IconPhone size={14} color="var(--mantine-color-gray-6)" />
              <Text size="sm" c={phoneContact ? 'gray.6' : 'gray.5'}>
                {phoneContact ? <ContactPointDisplay value={phoneContact} /> : '(---) --- ----'}
              </Text>
            </Group>
            {emailContact && (
              <Group gap={6}>
                <IconMail size={14} color="var(--mantine-color-gray-6)" />
                <Text size="sm" c="gray.6">
                  <ContactPointDisplay value={emailContact} />
                </Text>
              </Group>
            )}
          </Group>
        </Stack>
      </Group>

      <Group>
        <Menu shadow="md" width={200}>
          <Menu.Target>
            <Button
              variant="light"
              color="gold"
              rightSection={<IconChevronDown size={16} />}
            >
              Actions
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconPencil size={16} />}
              component={Link}
              to={`/dashboard/clients/${client.id}/profile`}
            >
              Edit
            </Menu.Item>
            {onToggleHighRisk && (
              <Menu.Item
                leftSection={<IconAlertTriangle size={16} />}
                color={isHighRisk ? undefined : 'red'}
                onClick={onToggleHighRisk}
              >
                {isHighRisk ? 'Remove High Risk' : 'Flag High Risk'}
              </Menu.Item>
            )}
            {onAddEpisode && (
              <Menu.Item
                leftSection={<IconPlus size={16} />}
                onClick={onAddEpisode}
              >
                Add EoC
              </Menu.Item>
            )}
            {onTransfer && (
              <Menu.Item
                leftSection={<IconTransfer size={16} />}
                onClick={onTransfer}
              >
                Transfer
              </Menu.Item>
            )}
          </Menu.Dropdown>
        </Menu>
      </Group>
    </Group>
  );
}
